/**
 * OWNER-OPERATING-SYSTEM-V1 -- Home (/dashboard/home).
 *
 * THE HOME RULING (growth brief s.19.1), implemented as product: the owner's
 * PRIMARY OPERATING PAGE, titled "Home" -- no role prefix. Default view =
 * the latest business summary (yesterday + month so far, P-1 from O-1/O-2)
 * plus urgent issues. The selector bar carries APPROVED filters only --
 * date range / clinic scope / department / comparison / saved view -- no
 * arbitrary widget layouts. Deep analysis stays in Reports (every band
 * drills there ONCE via its header, 25.10); the printable daily table is a
 * SEPARATE view reachable from Home, never inline.
 *
 * ARCHETYPE: DASHBOARD (ARCH law). Band rhythm (25.3): hero window band ->
 * needs-attention band (never collapses) -> month trend band -> quiet seam
 * strip -> operations row. REAL DATA ONLY: every number arrives from
 * /api/owner/home; absent facts render honest seams/absences (S-4 / SEAM),
 * never a fake numeral.
 *
 * Replaces the retired owner Overview (SURVIVORSHIP RULING: REPLACES;
 * SCREEN-TENANCY-AUDIT-V1 A1.1). The Overview's owner-only doors re-home
 * onto the operations row below -- capability preserved, zero read widened
 * (NAV law: a door is never a permission).
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { fetchOwnerHome } from '@/dashboard/api';
import type { OwnerHomeRange, OwnerHomeWire } from '@/dashboard/types';
import {
  AcBadge,
  AcButton,
  DataTable,
  EmptyState,
  KpiBand,
  LoadingState,
  ClinicScopeLabel,
  PageHeader,
  SeamChip,
  SectionBand,
  SelectorBar,
  type DataTableColumn,
  type KpiBandCellDef,
  type SelectorControlDef,
} from '@/dashboard/ui';
import DashboardLoadError from '@/dashboard/DashboardLoadError';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { useClinicLens } from '@/dashboard/clinic-context';
import {
  allIssueRows,
  businessDateLabel,
  comparisonBasis,
  dayCloseRows,
  deltaView,
  heroMoneyValue,
  inclusiveEndDate,
  monthSpark,
  preciseMoneyValue,
  printRows,
  windowTitle,
  type DayCloseRowView,
  type IssueRowView,
} from './owner-home-view';

const RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'month', label: 'This month' },
  { value: 'quarter', label: 'This quarter' },
] as const;

function ComparisonHistory({ wire }: { readonly wire: OwnerHomeWire }): JSX.Element {
  const money = (v: number | null): string =>
    v === null ? '--' : preciseMoneyValue(v);
  const count = (v: number | null): string => (v === null ? '--' : String(v));
  const pct = (v: number | null): string =>
    v === null ? '--' : `${v > 0 ? '+' : ''}${v}%`;
  return (
    <table className="table ac-table" aria-label="Like-date comparison">
      <thead>
        <tr>
          <th>Like window</th>
          <th className="ac-table__cell--end">Business</th>
          <th className="ac-table__cell--end">Walk-ins</th>
          <th className="ac-table__cell--end">Booked</th>
        </tr>
      </thead>
      <tbody>
        {wire.window.comparisons.map((c) => (
          <tr key={c.fromDate}>
            <td>{businessDateLabel(c.fromDate)}</td>
            <td className="ac-table__cell--end">
              {money(c.businessMinorUnits)} {pct(c.businessDeltaPct)}
            </td>
            <td className="ac-table__cell--end">
              {count(c.walkins)} {pct(c.walkinsDeltaPct)}
            </td>
            <td className="ac-table__cell--end">
              {count(c.booked)} {pct(c.bookedDeltaPct)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function issueColumns(): DataTableColumn<IssueRowView>[] {
  return [
    {
      key: 'issue',
      header: 'Issue',
      render: (row) => (
        <span>
          <span className={`ac-sev ac-sev--${row.severity === 'red' ? 'red' : 'amber'}`}>
            {row.glyph}
          </span>{' '}
          <strong>{row.headline}</strong>
        </span>
      ),
    },
    {
      key: 'owner',
      header: 'Owner',
      render: (row) => row.ownerLine,
    },
    {
      key: 'age',
      header: 'Waiting',
      align: 'end',
      render: (row) =>
        row.ageText === null ? (
          '--'
        ) : (
          <span className={`ac-age ac-age--${row.ageTone}`}>{row.ageText}</span>
        ),
    },
  ];
}

/** R-CD-B3: the day-close columns -- stored money-desk close facts, one
 *  row per (clinic x business day). '--' is the honest absent cell (the
 *  issues-table precedent); the difference cell carries the reason line so
 *  a short close explains itself in place (EVIDENCE ROWS). */
function dayCloseColumns(): DataTableColumn<DayCloseRowView>[] {
  return [
    { key: 'clinic', header: 'Clinic', render: (row) => row.clinicName },
    { key: 'day', header: 'Day (IST)', render: (row) => row.dayLabel },
    {
      key: 'status',
      header: 'Status',
      render: (row) =>
        row.status === 'closed' ? (
          <strong>{row.statusLabel}</strong>
        ) : (
          row.statusLabel
        ),
    },
    {
      key: 'counted',
      header: 'Counted',
      align: 'end',
      render: (row) => row.counted ?? '--',
    },
    {
      key: 'expected',
      header: 'Expected',
      align: 'end',
      render: (row) => row.expected ?? '--',
    },
    {
      key: 'difference',
      header: 'Difference',
      render: (row) =>
        row.differenceText === null ? (
          '--'
        ) : (
          <span>
            <AcBadge tone={row.differenceTone === 'exact' ? 'positive' : 'pending'}>
              {row.differenceText}
            </AcBadge>
            {row.reasonLine !== null ? (
              <>
                {' '}
                <span className="ac-dim">{row.reasonLine}</span>
              </>
            ) : null}
          </span>
        ),
    },
    {
      key: 'closedBy',
      header: 'Closed by',
      render: (row) => row.closedByLine ?? '--',
    },
  ];
}

function PrintSheet({
  wire,
  onBack,
}: {
  readonly wire: OwnerHomeWire;
  readonly onBack: () => void;
}): JSX.Element {
  const rows = printRows(wire);
  const priorLabels = wire.window.comparisons.map((c) => businessDateLabel(c.fromDate));
  const issueRows = allIssueRows(wire);
  return (
    <div data-testid="owner-home-print">
      <PageHeader
        title="Daily report"
        subs={[
          `${windowTitle(wire.window.label, wire.window.fromDate, wire.window.toDateExclusive)} -- generated ${new Date(wire.generatedAtIso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST`,
        ]}
      />
      <div className="ac-table-scroll">
        <table className="table ac-table" aria-label="Daily report">
          <thead>
            <tr>
              <th>Metric</th>
              <th className="ac-table__cell--end">Current</th>
              {priorLabels.map((l) => (
                <th key={l} className="ac-table__cell--end">
                  {l}
                </th>
              ))}
              <th className="ac-table__cell--end">vs prior</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.metric}>
                <td>{r.metric}</td>
                <td className="ac-table__cell--end">{r.current}</td>
                {priorLabels.map((l, i) => (
                  <td key={l} className="ac-table__cell--end">
                    {r.priors[i] ?? '--'}
                  </td>
                ))}
                <td className="ac-table__cell--end">{r.delta}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {issueRows.length > 0 ? (
        <p className="ac-table__count">
          Attention:{' '}
          {issueRows
            .map((r) =>
              r.ageText === null
                ? `${r.headline} (${r.ownerLine})`
                : `${r.headline} (${r.ownerLine}; waiting ${r.ageText})`,
            )
            .join('; ')}
          .
        </p>
      ) : null}
      <p className="ac-opsrow">
        <AcButton variant="secondary" onClick={() => window.print()}>
          Print
        </AcButton>
        <AcButton variant="ghost" onClick={onBack}>
          Back to Home
        </AcButton>
      </p>
    </div>
  );
}

export function OwnerHomeContent({
  wire,
  onOpenLeakage,
}: {
  readonly wire: OwnerHomeWire;
  readonly onOpenLeakage?: () => void;
}): JSX.Element {
  const heroCells: KpiBandCellDef[] = [
    {
      key: 'business',
      label: 'Business',
      value:
        wire.window.business === null
          ? 'Not available'
          : heroMoneyValue(wire.window.business.totalMinorUnits),
      delta: deltaView(wire.window.comparisons[0]?.businessDeltaPct ?? null),
      meta:
        wire.window.business === null
          ? 'money read unavailable right now'
          : `collected -- ${wire.window.business.transactionCount} payments`,
    },
    {
      key: 'walkins',
      label: 'Walk-ins',
      value: wire.window.walkins === null ? 'Not available' : String(wire.window.walkins),
      delta: deltaView(wire.window.comparisons[0]?.walkinsDeltaPct ?? null),
      meta: 'arrivals',
    },
    {
      key: 'booked',
      label: 'Booked',
      value: wire.window.booked === null ? 'Not available' : String(wire.window.booked),
      delta: deltaView(wire.window.comparisons[0]?.bookedDeltaPct ?? null),
      meta: 'appointments',
    },
    {
      key: 'accepted',
      label: 'Accepted',
      value:
        wire.window.accepted === null
          ? 'Not available'
          : String(wire.window.accepted.accepted),
      // OWN-01 (RECEPTION-FIX-AND-POLISH-BATCH-V1; the ONE owner-surface
      // exception, listed in files-changed): `accepted` and `advised` are
      // two INDEPENDENT decision-event counts over the window -- accepted
      // is NOT a subset of advised, so the old "N of M advised" fraction
      // could read "7 of 3". Two labeled facts, no implied denominator.
      meta:
        wire.window.accepted === null
          ? 'decision events unavailable'
          : `advised: ${wire.window.accepted.advised}`,
    },
  ];

  const issueRows = allIssueRows(wire);
  const spark = monthSpark(wire);
  const monthCells: KpiBandCellDef[] = [
    {
      key: 'business-mtd',
      label: 'Business',
      value:
        wire.monthToDate.businessMinorUnits === null
          ? 'Not available'
          : heroMoneyValue(wire.monthToDate.businessMinorUnits),
      delta: deltaView(wire.monthToDate.comparisons[0]?.businessDeltaPct ?? null),
      ...(spark ? { spark } : {}),
    },
    {
      key: 'walkins-mtd',
      label: 'Walk-ins',
      value:
        wire.monthToDate.walkins === null
          ? 'Not available'
          : String(wire.monthToDate.walkins),
      delta: deltaView(wire.monthToDate.comparisons[0]?.walkinsDeltaPct ?? null),
    },
  ];

  return (
    <div className="ac-band-flow" data-testid="owner-home-content">
      <KpiBand
        title={windowTitle(wire.window.label, wire.window.fromDate, wire.window.toDateExclusive)}
        {...(comparisonBasis(wire.window.comparisons)
          ? { basis: comparisonBasis(wire.window.comparisons)! }
          : {})}
        {...(wire.window.comparisons.length > 0
          ? {
              history: {
                summary: `${wire.comparisonWindowMonths}-month view`,
                content: <ComparisonHistory wire={wire} />,
              },
            }
          : {})}
        drill={<Link to="/dashboard/reports">in Reports</Link>}
        cells={heroCells}
        testId="owner-home-hero-band"
      />

      <SectionBand
        title="Needs attention"
        action={<Link to="/dashboard/leakage">Leakage board</Link>}
        testId="owner-home-attention-band"
      >
        <DataTable
          columns={issueColumns()}
          rows={issueRows}
          rowKey={(r) => r.key}
          {...(onOpenLeakage ? { onRowClick: () => onOpenLeakage() } : {})}
          countLine={
            issueRows.length > 0
              ? `${issueRows.length} open -- row opens the leakage board`
              : undefined
          }
          empty={
            <EmptyState
              title="No open issues right now."
              body="Leakage classes and today's clinic flags are clear. New breaches appear here as they age past their thresholds."
            />
          }
          testId="owner-home-issues"
        />
        <p className="ac-opsrow">
          <SeamChip
            label="Grievances: not tracked yet"
            explainer="Grievance capture (promise / complaint / review-risk) does not exist in the spine yet; grievance signals join this list when the capture package lands."
          />
        </p>
      </SectionBand>

      <SectionBand
        title="Money day close"
        basis="Per clinic, IST business days. Stored figures from the money desk close; cash never sums across clinics."
        action={<Link to="/dashboard/money">Open money desk</Link>}
        testId="owner-home-dayclose-band"
      >
        <DataTable
          columns={dayCloseColumns()}
          rows={dayCloseRows(wire)}
          rowKey={(r) => r.key}
          empty={
            <EmptyState
              title="No clinics in scope."
              body="Per-clinic day-close truth appears here once a money day is recorded."
            />
          }
          testId="owner-home-dayclose"
        />
      </SectionBand>

      <KpiBand
        title={`Month so far -- ${businessDateLabel(wire.monthToDate.fromDate)} to ${businessDateLabel(inclusiveEndDate(wire.monthToDate.toDateExclusive))}`}
        {...(comparisonBasis(wire.monthToDate.comparisons)
          ? { basis: comparisonBasis(wire.monthToDate.comparisons)! }
          : {})}
        drill={<Link to="/dashboard/reports">in Reports</Link>}
        cells={monthCells}
        testId="owner-home-month-band"
      />

      <p className="ac-opsrow" data-testid="owner-home-seamstrip">
        <SeamChip
          label="Departments: mapping pending"
          explainer="The catalog-category to department map (dental / cosmetology / other) is org configuration, founder-seeded in Studio. The split renders here once the map exists."
        />
        <SeamChip
          label="Assignments: not tracked yet"
          explainer="No assignment/task store exists in the spine. Due / completed / overdue / blocked per department renders here when the capture package lands."
        />
        <SeamChip
          label="Delivery: portal + print"
          explainer="Outbound messaging is the one lawful flag domain and its channel contract is unanswered; the daily read stays portal + print until then."
        />
      </p>
    </div>
  );
}

export function OwnerOperationsRow({
  showPatientMaster,
  showPatientMasterMerge,
  showTestJourney,
}: {
  readonly showPatientMaster: boolean;
  readonly showPatientMasterMerge: boolean;
  /** TEST-SPINE-GATING-V1: the owner test-journey door renders only on the
   *  server-computed testJourneyEnabled signal (gating ON: test-org pin +
   *  role set; gating OFF: the legacy owner audience emits it, so the link
   *  renders exactly as before -- the rollback contract). An ungated owner
   *  never sees a dead door (LES-016). */
  readonly showTestJourney: boolean;
}): JSX.Element {
  return (
    <SectionBand
      title="Operations"
      basis="read-only drills; seeing a queue never grants claim or assign authority"
      testId="owner-home-operations"
    >
      <p className="ac-opsrow">
        <Link to="/dashboard/agm">Manager tracking</Link>
        <Link to="/dashboard/lead-cockpit">Lead operations</Link>
        <Link to="/dashboard/org-intake">Org intake</Link>
        <Link to="/dashboard/lead-inbox">Lead inbox</Link>
        <Link to="/dashboard/clinical-workbench">Revenue confirm</Link>
        <Link to="/walkin">Clinic day board</Link>
        {showPatientMaster ? <Link to="/dashboard/patients">Patients lookup</Link> : null}
        {showPatientMasterMerge ? (
          <Link to="/dashboard/patients/merge-queue">Patient merges</Link>
        ) : null}
        {showTestJourney ? (
          <Link to="/dashboard/test-journey">Test journey</Link>
        ) : null}
      </p>
    </SectionBand>
  );
}

export default function OwnerHomePage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const meCtx = useDashboardMeContext();
  const showPatientMaster = meCtx?.me?.patientMasterEnabled === true;
  const showPatientMasterMerge = meCtx?.me?.patientMasterMergeEnabled === true;

  const range = (searchParams.get('range') ?? 'yesterday') as OwnerHomeRange;
  // 119-CLINIC-OPERATING-SPINE-V1 (Phase 2, founder ruling 4b). Owner Home is
  // an ANALYTICS surface: comparing branches is the owner's actual job at 119
  // clinics, so its ?clinicId is a REPORT PARAMETER ("what am I asking about"),
  // not the LENS ("where am I"). The ruling's four obligations, in order:
  //   1. it DEFAULTS TO THE LENS when no parameter is supplied (below);
  //   2. it STATES ON THE GLASS, IN WORDS, when the clinic it is showing
  //      differs from the lens (the scope slot below);
  //   3. the parameter is validated against the SAME reach list the lens is
  //      built from -- server-side, owner-home resolves it through
  //      ownerClinicIds, which is the very query listScopedClinicOptions runs
  //      to build the lens options, so a parameter cannot widen reach by one
  //      clinic; and
  //   4. it NEVER silently rewrites the lens -- this writes the URL only, so
  //      opening a shared link does not move the operator's whole session.
  const clinicCtx = useClinicLens();
  const lensClinicId = clinicCtx?.selectedClinicId ?? null;
  const clinicParam =
    searchParams.get('clinicId') ??
    (lensClinicId !== null ? String(lensClinicId) : null);
  const printView = searchParams.get('print') === '1';
  // HOME RULING: the org's FIRST saved view is the default lens. A clean
  // load (no explicit selector params) IS that lens -- the chip says so;
  // an explicit param means the user changed the lens (view param cleared
  // unless they re-picked a saved view).
  const explicitLens =
    searchParams.has('range') || searchParams.has('clinicId') || searchParams.has('view');
  const savedParam = explicitLens ? searchParams.get('view') : null;

  const [wire, setWire] = useState<OwnerHomeWire | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const retryToken = searchParams.get('retry');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchOwnerHome({
      range,
      ...(clinicParam ? { clinicId: Number.parseInt(clinicParam, 10) } : {}),
    })
      .then((data) => {
        if (cancelled) return;
        setWire(data);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load Home.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range, clinicParam, retryToken]);

  const setParam = (key: string, value: string | null): void => {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  // The clinic this REPORT is showing, named. "All clinics" is a real answer
  // here (an org-wide roll-up), not a missing selection.
  const shownClinicLabel: string =
    clinicParam === null
      ? 'All clinics'
      : ((wire?.clinics ?? []).find((c) => String(c.clinicId) === clinicParam)
          ?.name ?? 'Unnamed clinic');

  const controls: SelectorControlDef[] = useMemo(() => {
    const clinics = wire?.clinics ?? [];
    const savedViews = wire?.savedViews ?? [];
    const list: SelectorControlDef[] = [
      {
        key: 'range',
        label: 'Date range',
        options: RANGE_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
        value: range,
        onChange: (v) => setParam('range', v),
      },
      {
        key: 'scope',
        label: 'Clinic scope',
        options: [
          { value: '', label: 'All clinics' },
          ...clinics.map((c) => ({ value: String(c.clinicId), label: c.name })),
        ],
        value: clinicParam ?? '',
        onChange: (v) => setParam('clinicId', v === '' ? null : v),
      },
      {
        key: 'department',
        label: 'Department',
        options: [{ value: 'all', label: 'All departments' }],
        value: 'all',
      },
      {
        key: 'comparison',
        label: 'Comparison',
        options: [
          {
            value: 'like-date',
            label: `vs same window, last ${wire?.comparisonWindowMonths ?? 3} months`,
          },
        ],
        value: 'like-date',
      },
    ];
    if (savedViews.length > 0) {
      const defaultKey = savedViews[0]!.key;
      list.push({
        key: 'saved',
        label: 'Saved view',
        kind: 'saved',
        options: [
          ...savedViews.map((v) => ({ value: v.key, label: `Saved view: ${v.label}` })),
          { value: '', label: 'No saved view' },
        ],
        value: savedParam ?? (explicitLens ? '' : defaultKey),
        onChange: (v) => {
          const view = savedViews.find((s) => s.key === v);
          const next = new URLSearchParams(searchParams);
          if (!view) {
            next.delete('view');
          } else {
            next.set('view', view.key);
            next.set('range', view.dateRange);
            if (view.scope === 'all') next.delete('clinicId');
            else next.set('clinicId', view.scope);
          }
          setSearchParams(next, { replace: true });
        },
      });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wire, range, clinicParam, savedParam, searchParams]);

  if (loading && wire === null) {
    return (
      <div>
        <PageHeader title="Home" />
        <LoadingState label="Loading Home" />
      </div>
    );
  }

  if (error !== null && wire === null) {
    return (
      <div>
        <PageHeader title="Home" />
        <DashboardLoadError message={error} />
        <p className="ac-opsrow">
          <AcButton
            variant="secondary"
            onClick={() => setParam('retry', String(Date.now()))}
          >
            Retry
          </AcButton>
        </p>
      </div>
    );
  }

  if (wire === null) {
    return (
      <div>
        <PageHeader title="Home" />
        <LoadingState label="Loading Home" />
      </div>
    );
  }

  if (printView) {
    return <PrintSheet wire={wire} onBack={() => setParam('print', null)} />;
  }

  return (
    <div>
      <PageHeader
        title="Home"
        subs={[]}
        scope={
          <ClinicScopeLabel
            label={shownClinicLabel}
            mode="parameter"
            lensLabel={clinicCtx?.selectedLabel ?? null}
            testId="owner-home-clinic-scope"
          />
        }
        actions={
          <button
            type="button"
            className="ac-btn ac-btn--ghost"
            onClick={() => setParam('print', '1')}
          >
            Print view
          </button>
        }
      />
      <div style={{ margin: '0 0 var(--ac-space-4)' }}>
        <SelectorBar controls={controls} testId="owner-home-selbar" />
      </div>
      <OwnerHomeContent
        wire={wire}
        onOpenLeakage={() => navigate('/dashboard/leakage')}
      />
      <div style={{ marginTop: 'var(--ac-space-4)' }}>
        <OwnerOperationsRow
          showPatientMaster={showPatientMaster}
          showPatientMasterMerge={showPatientMasterMerge}
          showTestJourney={meCtx?.me?.testJourneyEnabled === true}
        />
      </div>
    </div>
  );
}
